import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
} from 'recharts';

const chartData = [
  { trade: 0, median: 0, p25: -1.5, p75: 1.5, live: 0 },
  { trade: 1, median: 0.8, p25: -1.0, p75: 2.5, live: -0.5 },
  { trade: 2, median: 1.5, p25: -0.5, p75: 3.8, live: -1.2 },
  { trade: 3, median: 2.2, p25: 0, p75: 5.0, live: 0.4 },
  { trade: 4, median: 3.0, p25: 0.5, p75: 6.0, live: 1.2 },
  { trade: 5, median: 3.5, p25: 1.0, p75: 6.8, live: 1.8 },
  { trade: 6, median: 4.0, p25: 1.5, p75: 7.5, live: 2.5 },
  { trade: 7, median: 4.8, p25: 2.0, p75: 8.2, live: 3.2 },
  { trade: 8, median: 5.5, p25: 2.5, p75: 9.0, live: 4.0 },
  { trade: 9, median: 6.2, p25: 3.0, p75: 9.5, live: 5.2 },
  { trade: 10, median: 6.8, p25: 3.5, p75: 10.2, live: 5.8 },
  { trade: 11, median: 7.5, p25: 4.0, p75: 11.0, live: 6.5 },
  { trade: 12, median: 8.0, p25: 4.5, p75: 11.5, live: 7.8 },
];

const EquityCurveRiskMock = () => (
  <div className="space-y-4">
    {/* Cumulative Return Comparison chart mock */}
    <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono" style={{ background: '#0f1923' }}>
      <div className="p-3">
        <div className="bg-[#181825] border border-white/[0.06] rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faChartLine} className="text-sm text-blue-400" />
              <h3 className="text-sm font-medium text-white uppercase tracking-wide">Cumulative Return Comparison</h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
              Live (12/18)
            </div>
          </div>

          <div className="h-[220px] bg-gray-900/30 rounded border border-white/[0.04] pt-1">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                <XAxis dataKey="trade" tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#374151', strokeOpacity: 0.3 }} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `${v}%`} domain={[-3, 13]} />
                <Tooltip
                  contentStyle={{ background: '#1e1e2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, fontSize: 11, color: '#e5e7eb' }}
                  labelStyle={{ color: '#e5e7eb' }}
                  itemStyle={{ color: '#e5e7eb' }}
                  labelFormatter={(v: number) => `Trade #${v}`}
                  formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name === 'p25' ? 'p25' : name === 'p75' ? 'p75' : name === 'median' ? 'Expected' : 'Live']}
                />
                <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="6 3" strokeOpacity={0.5} />
                <Area dataKey="p75" stroke="none" fill="#60a5fa" fillOpacity={0.12} isAnimationActive={false} />
                <Area dataKey="p25" stroke="none" fill="#0f1923" fillOpacity={1} isAnimationActive={false} />
                <Line dataKey="median" stroke="#60a5fa" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line dataKey="live" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981', stroke: '#0f1923', strokeWidth: 1 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>

    {/* Risk Tolerance -- matches RiskTolerancePanel.tsx */}
    <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono" style={{ background: '#0f1923' }}>
      <div className="p-3">
        <div className="bg-[#181825] border border-white/[0.06] rounded-lg p-3">
          <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
            Risk Tolerance
          </h4>

          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">Risk Level</span>
              <span className="font-medium text-gray-400">Unknown</span>
            </div>
            <div className="h-2 rounded-full bg-gray-700 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 via-amber-500 via-orange-500 to-rose-500"
                style={{ width: '35%', backgroundSize: '100vw 100%', backgroundPosition: 'left' }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-600">
              <span>Low</span>
              <span>Moderate</span>
              <span>High</span>
              <span>Extreme</span>
            </div>
          </div>

          <div className="flex justify-between mt-3 text-xs">
            <div>
              <span className="text-gray-500">Drawdown recovery: </span>
              <span className="text-gray-300">~11 months</span>
            </div>
            <div>
              <span className="text-gray-500">Re-opt: </span>
              <span className="text-gray-300">Every 1.7 year</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default EquityCurveRiskMock;
